/**
 * Surface materials authored on track meshes (via GLTF extras `surface`).
 * Determines grip, drag, and any per-surface effects (boost, slip).
 *
 * Add new surfaces here; the build pipeline (tools/track-pipeline) validates
 * that every authored mesh's `extras.surface` resolves to one of these names.
 */
export const SURFACE_NAMES = [
  'road',
  'dirt',
  'grass',
  'ice',
  'boost',
  'wall',
] as const;
export type SurfaceName = (typeof SURFACE_NAMES)[number];

export interface SurfaceProps {
  /** Lateral grip multiplier 0..1. Lower = more slide. */
  grip: number;
  /** Linear drag coefficient applied to forward velocity per second. */
  drag: number;
  /** Per-second forward accel bonus while in contact (boost strips). */
  boostAccel: number;
  /** Roll resistance (engine-off coast deceleration), m/s². */
  rollResist: number;
}

export const SURFACES: Record<SurfaceName, SurfaceProps> = {
  road: { grip: 0.95, drag: 0.05, boostAccel: 0, rollResist: 1.0 },
  dirt: { grip: 0.6, drag: 0.18, boostAccel: 0, rollResist: 2.5 },
  grass: { grip: 0.4, drag: 0.35, boostAccel: 0, rollResist: 4.0 },
  ice: { grip: 0.1, drag: 0.02, boostAccel: 0, rollResist: 0.5 },
  boost: { grip: 0.95, drag: 0.05, boostAccel: 80, rollResist: 0 },
  // Wall is here only so a wall hit can be looked up by the same path;
  // vehicles never "drive on" walls — collisions are resolved as slides.
  wall: { grip: 0, drag: 0, boostAccel: 0, rollResist: 0 },
};

export const DEFAULT_SURFACE: SurfaceName = 'road';

export const surfaceIdToName = (id: number): SurfaceName => {
  const name = SURFACE_NAMES[id];
  return name ?? DEFAULT_SURFACE;
};
export const surfaceNameToId = (name: SurfaceName): number => SURFACE_NAMES.indexOf(name);
